import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Plus, GripVertical, Check, ChevronDown } from 'lucide-react';
import { Modal, Select, Button } from '../../../components';
import { domiciliaryPackagesService } from '../../../services';
import { PackageFormSkeleton } from '../../../skeletons';
import { toast } from 'react-hot-toast';
import { todayKey } from '../../../utils/datetime';

const VISIT_TYPES = ['Morning Call', 'Lunch Call', 'Tea Call', 'Afternoon', 'Evening Check', 'Night'];
const DAYS = [
  { key: 'Monday', label: 'M' }, { key: 'Tuesday', label: 'T' }, { key: 'Wednesday', label: 'W' },
  { key: 'Thursday', label: 'T' }, { key: 'Friday', label: 'F' }, { key: 'Saturday', label: 'S' }, { key: 'Sunday', label: 'S' },
];
const TASK_LEVELS = {
  required: { label: 'REQUIRED', cls: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300' },
  if_needed: { label: 'IF NEEDED', cls: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300' },
  optional: { label: 'OPTIONAL', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-300' },
};
const levelOf = (t) => (t && TASK_LEVELS[t.level] ? t.level : (t && t.mandatory ? 'required' : 'optional'));
const minutesBetween = (s, e) => {
  if (!s || !e) return '';
  const [sh, sm] = s.split(':').map(Number); const [eh, em] = e.split(':').map(Number);
  let d = (eh * 60 + em) - (sh * 60 + sm); if (d < 0) d += 1440; return String(d);
};
// Human duration: "45m", "1h", "1h 30m".
const fmtDuration = (mins) => {
  const n = parseInt(mins, 10);
  if (!n) return '—';
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60); const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};
// Current wall-clock time as "HH:MM", and a "HH:MM" + minutes helper — used to
// default a NEW package's visit window to "now → now + 45m".
const pad2 = (n) => String(n).padStart(2, '0');
const nowHM = () => { const d = new Date(); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const addMins = (hm, mins) => {
  const [h, m] = hm.split(':').map(Number);
  const total = (((h * 60 + m + mins) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

const Field = ({ label, children, hint }) => (
  <div className="min-w-0">
    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">{hint}</p>}
  </div>
);
const inputCls = 'h-11 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none';

// Add / Edit a care package. Fields mirror the prototype's form. zones + carers
// are passed in from the screen; service users are fetched here.
const PackageFormModal = ({ open, packageId, fromDate, zones = [], carers = [], defaultDate, onClose, onSaved }) => {
  const editing = !!packageId;
  const [serviceUsers, setServiceUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefOpen, setPrefOpen] = useState(false);
  const prefRef = useRef(null);

  // Close the preferred-carers dropdown when clicking outside it.
  useEffect(() => {
    const onClick = (e) => { if (prefRef.current && !prefRef.current.contains(e.target)) setPrefOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const [form, setForm] = useState(() => {
    const start = nowHM();
    return {
      package_name: '', residence_id: '', travel_zone: '', travel_area: '',
      start_date: defaultDate || todayKey(), end_date: '',
      visit_type: 'Morning Call', visit_time: start, visit_end_time: addMins(start, 45),
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      tasks: [
        { text: 'Personal care', level: 'if_needed' },
        { text: 'Medication prompt', level: 'required' },
        { text: 'Meal preparation', level: 'optional' },
      ],
      preferred_carers: [], initial_assignment: '',
    };
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const realZones = useMemo(() => zones.filter((z) => z.id != null), [zones]);
  // Initial assignment is chosen from the preferred pool.
  const preferredCarerObjs = carers.filter((c) => form.preferred_carers.includes(c.id));
  const togglePreferred = (id) => setForm((f) => {
    const has = f.preferred_carers.includes(id);
    const preferred_carers = has ? f.preferred_carers.filter((x) => x !== id) : [...f.preferred_carers, id];
    // If the removed carer was the initial assignment, clear it.
    const initial_assignment = (has && Number(f.initial_assignment) === id) ? '' : f.initial_assignment;
    return { ...f, preferred_carers, initial_assignment };
  });
  const selectedUser = serviceUsers.find((u) => String(u.id) === String(form.residence_id));
  const duration = minutesBetween(form.visit_time, form.visit_end_time);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const tasks = [domiciliaryPackagesService.getServiceUsers()];
    if (editing) tasks.push(domiciliaryPackagesService.getPackage(packageId));
    Promise.all(tasks)
      .then(([usersRes, pkgRes]) => {
        if (usersRes.success) setServiceUsers(usersRes.data || []);
        if (pkgRes && pkgRes.success) {
          const p = pkgRes.data;
          setForm((f) => ({
            ...f,
            package_name: p.package_name || '',
            residence_id: p.residence_id || '',
            travel_zone: p.travel_zone || '',
            travel_area: '',
            start_date: fromDate || (p.start_date ? String(p.start_date).slice(0, 10) : f.start_date),
            end_date: p.end_date ? String(p.end_date).slice(0, 10) : '',
            visit_type: p.visit_type || 'Morning Call',
            visit_time: p.visit_time || '07:30',
            visit_end_time: p.visit_end_time || '08:15',
            days: Array.isArray(p.visit_pattern) ? p.visit_pattern : f.days,
            tasks: (p.tasks || []).map((t) => ({ text: t.text || t.task_text || '', level: levelOf(t) })),
            preferred_carers: Array.isArray(p.preferred_carers) ? p.preferred_carers.map(Number) : [],
            initial_assignment: p.initial_assignment ? String(p.initial_assignment) : '',
          }));
        }
      })
      .catch(() => toast.error('Failed to load form data'))
      .finally(() => setLoading(false));
  }, [open, editing, packageId]);

  // Auto-fill zone + area from the chosen client's postcode (if a zone matches).
  const onPickUser = (id) => {
    const user = serviceUsers.find((u) => String(u.id) === String(id));
    let patch = { residence_id: id };
    if (user?.postcode) {
      const pc = user.postcode.toUpperCase().replace(/\s+/g, '');
      const z = realZones.find((zz) => (zz.postcode_prefixes || []).some((p) => pc.startsWith(String(p).toUpperCase().replace(/\s+/g, ''))));
      if (z) patch = { ...patch, travel_zone: String(z.id), travel_area: (z.postcode_prefixes || []).join(', ') };
    }
    set(patch);
  };

  const onPickZone = (id) => {
    const z = realZones.find((zz) => String(zz.id) === String(id));
    set({ travel_zone: id, travel_area: z ? (z.postcode_prefixes || []).join(', ') : form.travel_area });
  };

  const cycleLevel = (i) => {
    const order = ['required', 'if_needed', 'optional'];
    setForm((f) => {
      const tasks = [...f.tasks];
      tasks[i] = { ...tasks[i], level: order[(order.indexOf(levelOf(tasks[i])) + 1) % order.length] };
      return { ...f, tasks };
    });
  };
  const setTaskText = (i, text) => setForm((f) => { const t = [...f.tasks]; t[i] = { ...t[i], text }; return { ...f, tasks: t }; });
  const addTask = () => set({ tasks: [...form.tasks, { text: '', level: 'optional' }] });
  const removeTask = (i) => set({ tasks: form.tasks.filter((_, idx) => idx !== i) });
  const toggleDay = (key) => set({ days: form.days.includes(key) ? form.days.filter((d) => d !== key) : [...form.days, key] });

  const save = async () => {
    if (!form.residence_id) { toast.error('Pick a service user'); return; }
    if (form.days.length === 0) { toast.error('Pick at least one day'); return; }
    const payload = {
      package_name: form.package_name,
      residence_id: form.residence_id,
      travel_zone: form.travel_zone || null,
      travel_area: form.travel_area ? form.travel_area.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) : undefined,
      start_date: form.start_date,
      end_date: form.end_date || null,
      visit_type: form.visit_type,
      visit_time: form.visit_time,
      visit_end_time: form.visit_end_time,
      duration,
      visit_pattern: form.days,
      tasks: form.tasks.filter((t) => t.text.trim()).map((t) => ({ text: t.text.trim(), level: levelOf(t), mandatory: levelOf(t) === 'required' })),
      preferred_carers: form.preferred_carers,
      initial_assignment: form.initial_assignment || null,
      from_date: fromDate || undefined,
    };
    setSaving(true);
    try {
      const res = editing
        ? await domiciliaryPackagesService.updatePackage(packageId, payload)
        : await domiciliaryPackagesService.createPackage(payload);
      if (res.success) { toast.success(editing ? 'Care package updated' : 'Care package created'); onSaved(); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error saving package');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={onClose} hideHeader bodyClassName="flex flex-col flex-1 min-h-0" maxWidth="max-w-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 px-6 py-4">
          <h2 className="text-[17px] font-semibold text-gray-800 dark:text-gray-200">{editing ? (fromDate ? `Edit Care Package — from ${fromDate}` : 'Edit Care Package') : 'Add Care Package'}</h2>
          <button onClick={onClose} className="grid h-6 w-6 place-items-center rounded-full bg-blue-600 text-white hover:bg-blue-700"><X className="h-3.5 w-3.5" strokeWidth={3} /></button>
        </div>

        {loading ? (
          <PackageFormSkeleton />
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <Field label="Care package name">
              <input value={form.package_name} onChange={(e) => set({ package_name: e.target.value })} placeholder="e.g. Morning support" className={inputCls} />
            </Field>

            {/* Service user */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c] p-3">
              <Field label="Service user">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Select
                    className="w-full"
                    value={form.residence_id}
                    onChange={onPickUser}
                    placeholder="Select service user…"
                    options={serviceUsers.map((u) => ({ value: String(u.id), label: `${u.name}${u.code ? ` · ${u.code}` : ''}` }))}
                  />
                  <div className="rounded-lg bg-blue-50/60 dark:bg-blue-900/10 px-3 py-2 text-[11px] text-gray-500 dark:text-gray-400">
                    {selectedUser ? (
                      <>
                        <div className="font-semibold text-gray-700 dark:text-gray-200">{selectedUser.address || 'No address on file'}</div>
                        <div>{selectedUser.postcode || ''}</div>
                      </>
                    ) : 'Address shows here'}
                  </div>
                </div>
              </Field>
            </div>

            {/* Zone + area */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Travel zone" hint="Choose a saved zone (manage zones from the Schedule).">
                <Select
                  className="w-full"
                  value={form.travel_zone}
                  onChange={onPickZone}
                  placeholder="No zone"
                  options={[{ value: '', label: 'No zone' }, ...realZones.map((z) => ({ value: String(z.id), label: z.name }))]}
                />
              </Field>
              <Field label="Travel area" hint="Saved onto the zone for future packages.">
                <input value={form.travel_area} onChange={(e) => set({ travel_area: e.target.value })} placeholder="e.g. M20, M21" className={inputCls} />
              </Field>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Start date"><input type="date" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} className={inputCls} /></Field>
              <Field label="End date (optional)"><input type="date" value={form.end_date} onChange={(e) => set({ end_date: e.target.value })} className={inputCls} /></Field>
            </div>

            {/* Visit pattern */}
            <div>
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Visit pattern</div>
              <div className="grid grid-cols-2 gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c] p-3 sm:grid-cols-4">
                <Field label="Type">
                  <Select
                    className="w-full"
                    value={form.visit_type}
                    onChange={(v) => set({ visit_type: v })}
                    options={VISIT_TYPES.map((t) => ({ value: t, label: t }))}
                  />
                </Field>
                <Field label="Start time"><input type="time" value={form.visit_time} onChange={(e) => set({ visit_time: e.target.value })} className={`${inputCls} bg-white`} /></Field>
                <Field label="End time"><input type="time" value={form.visit_end_time} onChange={(e) => set({ visit_end_time: e.target.value })} className={`${inputCls} bg-white`} /></Field>
                <Field label="Duration"><div className="flex h-11 items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] px-3 text-sm font-bold text-gray-700 dark:text-gray-200">{fmtDuration(duration)}</div></Field>
              </div>
              <div className="mt-3 flex gap-2">
                {DAYS.map((d) => (
                  <button key={d.key} onClick={() => toggleDay(d.key)}
                    className={`grid h-10 w-10 place-items-center rounded-lg text-sm font-bold transition ${form.days.includes(d.key) ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-[#31373f] text-gray-400 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#3a404a]'}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tasks */}
            <div>
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Task checklist</div>
              <div className="space-y-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c] p-3">
                {form.tasks.map((t, i) => {
                  const lvl = levelOf(t);
                  return (
                    <div key={i} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] px-2 py-2">
                      <GripVertical className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                      <input value={t.text} onChange={(e) => setTaskText(i, e.target.value)} placeholder="Task name"
                        className="min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none" />
                      <button onClick={() => cycleLevel(i)} className={`rounded px-2 py-1 text-[9px] font-bold uppercase ${TASK_LEVELS[lvl].cls}`}>{TASK_LEVELS[lvl].label}</button>
                      <button onClick={() => removeTask(i)} className="text-gray-300 dark:text-gray-600 hover:text-rose-500"><X className="h-4 w-4" /></button>
                    </div>
                  );
                })}
                <button onClick={addTask} className="inline-flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:text-blue-700"><Plus className="h-3.5 w-3.5" /> Add Custom Task</button>
              </div>
            </div>

            {/* Carers */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Carers</span>
                <span className="text-[11px] text-gray-400 dark:text-gray-500">Zone-matched carers are recommended during assignment.</span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Preferred carers" hint="Pick one or more — used as recommendations when assigning.">
                  <div className="relative" ref={prefRef}>
                    <button type="button" onClick={() => setPrefOpen((o) => !o)}
                      className={`${inputCls} flex items-center justify-between text-left`}>
                      <span className={`truncate ${preferredCarerObjs.length ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}>
                        {preferredCarerObjs.length === 0 ? 'Select carers…'
                          : preferredCarerObjs.length <= 2 ? preferredCarerObjs.map((c) => c.name).join(', ')
                          : `${preferredCarerObjs.length} selected`}
                      </span>
                      <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500 transition ${prefOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {prefOpen && (
                      <div className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] p-1 shadow-lg">
                        {carers.length === 0 && <div className="py-3 text-center text-xs text-gray-400 dark:text-gray-500">No carers available.</div>}
                        {carers.map((c) => {
                          const sel = form.preferred_carers.includes(c.id);
                          return (
                            <button key={c.id} type="button" onClick={() => togglePreferred(c.id)}
                              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left ${sel ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}>
                              <span className={`grid h-5 w-5 place-items-center rounded border ${sel ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300 dark:border-gray-600'}`}>
                                {sel && <Check className="h-3 w-3" strokeWidth={3} />}
                              </span>
                              <span className="text-sm text-gray-700 dark:text-gray-200">{c.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Field>
                <Field label="Initial assignment" hint="Assign now, or leave unassigned to assign later from the board.">
                  <Select
                    className="w-full"
                    value={form.initial_assignment}
                    onChange={(v) => set({ initial_assignment: v })}
                    disabled={preferredCarerObjs.length === 0}
                    placeholder="Unassigned (assign later)"
                    options={[{ value: '', label: 'Unassigned (assign later)' }, ...preferredCarerObjs.map((c) => ({ value: String(c.id), label: c.name }))]}
                  />
                  {preferredCarerObjs.length === 0 && <p className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">Pick a preferred carer first to assign one now.</p>}
                </Field>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 dark:border-gray-800 px-6 py-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save Care Package' : 'Create Care Package'}
          </Button>
        </div>
    </Modal>
  );
};

export default PackageFormModal;
